import * as React from "react";
import { ListFilter, SlidersHorizontal } from "lucide-react";
import { Button } from "../../../ui/components/Button";
import type { DataCatalogAlert } from "../../lib/api/products";
import {
  listActiveExceptions,
  partitionAlerts,
  type AlertPrefsDraft,
} from "./productAlertSubscription";
import { AlertSeverityToggles } from "./AlertSeverityToggles";
import { ProductAlertConfigureModal } from "./ProductAlertConfigureModal";
import * as s from "./ProductSubscriptionsModal.css";

export type ProductAlertPrefsPanelProps = {
  prefs: AlertPrefsDraft;
  onChange: (next: AlertPrefsDraft) => void;
  baselinePrefs?: AlertPrefsDraft | null;
  onSave: (prefs: AlertPrefsDraft) => Promise<void>;
  alerts: DataCatalogAlert[];
  alertsLoading?: boolean;
  disabled?: boolean;
  productName?: string;
};

export function ProductAlertPrefsPanel({
  prefs,
  onChange,
  baselinePrefs = null,
  onSave,
  alerts,
  alertsLoading,
  disabled,
  productName,
}: ProductAlertPrefsPanelProps) {
  const [configureOpen, setConfigureOpen] = React.useState(false);

  const activeAlerts = React.useMemo(
    () => alerts.filter((a) => a.isActive !== false),
    [alerts],
  );

  const { allowed, blocked } = React.useMemo(
    () => partitionAlerts(prefs, activeAlerts),
    [prefs, activeAlerts],
  );

  const exceptions = React.useMemo(
    () => listActiveExceptions(prefs, activeAlerts),
    [prefs, activeAlerts],
  );

  const exceptionTooltip =
    exceptions.length === 0
      ? undefined
      : exceptions
          .map(
            (e) =>
              `${e.code} (${e.mode === "allow" ? "allowed" : "blocked"}) — ${e.name}`,
          )
          .join("\n");

  const summaryParts = alertsLoading
    ? ["Loading alerts…"]
    : [
        `${allowed.length} allowed`,
        `${blocked.length} blocked`,
        ...(exceptions.length > 0
          ? [
              `${exceptions.length} ${
                exceptions.length === 1 ? "exception" : "exceptions"
              }`,
            ]
          : []),
      ];
  const summaryLabel = summaryParts.join(" · ");
  const summaryTitle = [
    summaryLabel,
    exceptionTooltip ? `Exceptions:\n${exceptionTooltip}` : null,
  ]
    .filter(Boolean)
    .join("\n\n");

  return (
    <>
      <div className={s.alertPrefsPanel}>
        <div className={s.alertSeverityRow}>
          <AlertSeverityToggles
            prefs={prefs}
            onChange={onChange}
            baselinePrefs={baselinePrefs}
            disabled={disabled}
          />
          <span
            className={s.alertSummaryBadge}
            title={summaryTitle}
            aria-label={summaryLabel}
          >
            <ListFilter size={12} />
            {summaryLabel}
          </span>
          <div className={s.alertSeverityTrailing}>
            <Button
              type="button"
              size="sm"
              color="primary"
              variant="outline"
              iconLeft={<SlidersHorizontal size={14} />}
              disabled={disabled}
              onClick={() => setConfigureOpen(true)}
            >
              Configure alerts
            </Button>
          </div>
        </div>
      </div>

      <ProductAlertConfigureModal
        open={configureOpen}
        setOpen={setConfigureOpen}
        prefs={prefs}
        baselinePrefs={baselinePrefs}
        onSave={onSave}
        alerts={alerts}
        alertsLoading={alertsLoading}
        disabled={disabled}
        productName={productName}
      />
    </>
  );
}
